import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Plus, Minus, ArrowUpRight, Shield, Star, Quote,
  Award, Users, CheckCircle2, Clock,
  Search, Map, FileText, Landmark, LineChart, ShieldCheck
} from "lucide-react";

const services = [
  { icon: ShieldCheck, tag: "Most Popular", title: "Credit Restoration Consulting",
    desc: "Strategic dispute management and bureau communication to remove inaccurate, outdated, or unverifiable items from your credit report." },
  { icon: LineChart, tag: null, title: "Credit-Building Strategy",
    desc: "Secured cards, credit builder loans, and authorized user guidance — a personalized roadmap to grow your score the right way." },
  { icon: Landmark, tag: "High Demand", title: "Lender-Ready Preparation",
    desc: "We prepare you for mortgage, auto, and business loan approvals — optimizing your profile before you walk into a lender's office." },
  { icon: FileText, tag: null, title: "Budget & DTI Analysis",
    desc: "Deep-dive into your debt-to-income ratio and spending patterns to identify what's holding you back from approval." },
];

const process = [
  { num: "01", icon: Search, title: "Free Credit Review",
    text: "We pull and analyze your full credit report — identifying every negative item, error, and opportunity for improvement. No cost, no commitment." },
  { num: "02", icon: Map, title: "Personalized Action Plan",
    text: "We build a custom roadmap tailored to your goals — whether that's a mortgage, auto loan, or business financing. Every step is clear and actionable." },
  { num: "03", icon: Users, title: "We Execute Together",
    text: "We handle disputes, coach you on credit-building moves, and monitor your progress every step of the way. You're never alone in this process." },
  { num: "04", icon: CheckCircle2, title: "You Get Approved",
    text: "With a stronger credit profile and lender-ready preparation, you walk into your approval with confidence. This is the mission — and we complete it." },
];

const testimonials = [
  { name: "Marcus T.", loc: "Auto Loan Approved · Dallas, TX", score: "498 → 661",
    quote: "I was denied twice for a car loan. Infantry Financial gave me a plan, executed with me step by step, and 5 months later I drove off the lot. This is real." },
  { name: "Priya & James R.", loc: "First Home · Houston, TX", score: "521 → 694",
    quote: "After years of thinking homeownership was impossible for us, Infantry Financial showed me exactly what needed to change. We closed on our first home in 8 months." },
  { name: "DeShawn W.", loc: "Business Line · Austin, TX", score: "520 → 680",
    quote: "I had collections, late payments, everything. They didn't judge me — they just got to work. My score went from 520 to 680 in 6 months." },
  { name: "Sandra M.", loc: "Credit Rebuild · San Antonio, TX", score: "545 → 710",
    quote: "The transparency was what got me. No hidden fees, no vague promises. They told me exactly what they could do and then they did it." },
];

const faqs = [
  { q: "How is Infantry Financial different from other credit repair companies?",
    a: "Most credit repair companies send generic dispute letters and charge you monthly regardless of results. We take a consulting approach — we analyze your specific situation, build a custom strategy, and work alongside you every step of the way. We're CROA compliant, and we're transparent about what we can and cannot do." },
  { q: "Do you offer a guarantee?",
    a: "Yes. We back our work with a 90-day money-back guarantee — if we don't get at least one negative item deleted from your credit report within 90 days, we'll issue a full refund. Simple, fair, and on us if we don't deliver." },
  { q: "How long does it take to see results?",
    a: "Most clients see measurable score movement within 30–60 days. A typical full restoration runs 3–6 months depending on the complexity of your report and the specific negative items being disputed." },
  { q: "Is credit restoration legal?",
    a: "Yes. Disputing inaccurate, outdated, or unverifiable information on your credit report is your federally protected right under the FCRA." },
  { q: "What if my credit doesn't improve?",
    a: "We're transparent about what we can and cannot do. If your report is 100% accurate with legitimate negative items, restoration may not help — and we'll be upfront with you about that during your consultation." },
  { q: "Do I need to be in Texas to work with you?",
    a: "We primarily serve Texas clients because we understand the Texas market and lender landscape, but we can work with anyone in the U.S. who wants a disciplined consulting approach." },
];

function Stat({ num, label }) {
  return (
    <div className="border-l-2 border-[#3F4A1D]/40 pl-4">
      <p className="font-bebas text-[#23261C] text-3xl md:text-4xl leading-none" style={{ letterSpacing: "0.05em" }}>{num}</p>
      <p className="text-[#5B5F50] text-xs tracking-widest uppercase mt-2" style={{ fontFamily: "Inter, sans-serif" }}>{label}</p>
    </div>
  );
}

export default function Home() {
  const [openFaq, setOpenFaq] = React.useState(0);
  return (
    <>
      {/* HERO — clean cream, bold condensed headline (reference style) */}
      <section className="relative min-h-[88vh] flex flex-col justify-center bg-[#F5F4EF]">
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-24 pb-20">
          <div className="max-w-5xl fade-up">
            <span className="chip mb-10" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3F4A1D] inline-block" />
              FOR CREDIT RESTORATION &amp; FINANCIAL CONSULTING
            </span>
            <h1 className="font-hero text-[#23261C] mb-8" style={{ fontSize: "clamp(44px, 5.8vw, 86px)" }}>
              YOUR CREDIT.<br />
              <span className="text-[#5C6B21]">REBUILT WITH MILITARY</span><br />
              PRECISION.
            </h1>
            <p className="text-[#4A4E40] text-lg md:text-xl leading-relaxed mb-10 max-w-xl" style={{ fontFamily: "Inter, sans-serif" }}>
              We help real people in Texas restore their credit, qualify for loans, and
              achieve financial freedom — with discipline, transparency, and zero empty promises.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-16">
              <Link to="/booking" className="btn-gold" data-testid="hero-cta-book">
                Get Started <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/services" className="btn-outline" data-testid="hero-cta-about">
                See services &amp; pricing
              </Link>
            </div>

            <div className="flex flex-wrap gap-10">
              <Stat num="500+" label="Clients Helped" />
              <Stat num="8+" label="Years in Business" />
              <Stat num="4.9★" label="Client Rating" />
              <Stat num="CROA" label="Compliant" />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES — soft cream */}
      <section className="bg-[#EFEDE5] py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-[#4F5D1E] text-xs font-semibold tracking-widest uppercase mb-3" style={{ fontFamily: "Inter, sans-serif" }}>INFANTRY / FINANCIAL</p>
              <h2 className="font-hero text-4xl md:text-5xl text-[#23261C] leading-tight">Our Services</h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 text-[#23261C] font-semibold text-sm tracking-wide hover:text-[#4F5D1E] transition-colors" style={{ fontFamily: "Inter, sans-serif" }} data-testid="home-services-link">
              View All Services <ArrowUpRight className="w-4 h-4 text-[#4F5D1E]" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="inverse bg-[#3A451C] rounded-2xl p-8 flex flex-col justify-between min-h-[280px]">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#B9C77F]/20 border border-[#B9C77F]/30">
                <Award className="text-[#B9C77F] w-5 h-5" />
              </div>
              <div>
                <p className="font-bebas text-[#DDE5BB] leading-none mb-2" style={{ fontSize: "clamp(48px,6vw,72px)", letterSpacing: "0.04em" }}>520 → 680</p>
                <p className="text-white/60 text-sm" style={{ fontFamily: "Inter, sans-serif" }}>Average client score improvement in 6 months</p>
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  {["Mortgage", "Auto", "Business"].map((t) => (
                    <span key={t} className="text-[#DDE5BB]/90 text-xs tracking-wider uppercase border border-[#B9C77F]/30 px-2.5 py-1 rounded-full" style={{ fontFamily: "Inter, sans-serif" }}>{t}</span>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {services.map((s, i) => (
                <div key={s.title} className="bg-white rounded-2xl p-6 hover:shadow-md transition-all duration-200 group cursor-pointer" data-testid={`service-card-${i}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#EFEDE5]">
                      <s.icon className="text-[#4F5D1E] w-5 h-5" />
                    </div>
                    {s.tag && (
                      <span className="text-[10px] font-semibold tracking-widest uppercase bg-[#4F5D1E]/10 text-[#4F5D1E] px-2 py-1 rounded-full" style={{ fontFamily: "Inter, sans-serif" }}>{s.tag}</span>
                    )}
                  </div>
                  <h3 className="font-display text-[#23261C] font-bold text-base mb-2 group-hover:text-[#4F5D1E] transition-colors">{s.title}</h3>
                  <p className="text-[#555] text-sm leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS — cream with white cards */}
      <section className="bg-[#F5F4EF] py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <p className="text-[#4F5D1E] text-xs font-semibold tracking-widest uppercase mb-3" style={{ fontFamily: "Inter, sans-serif" }}>THE PROCESS</p>
            <h2 className="font-hero text-4xl md:text-5xl text-[#23261C] leading-tight">How It Works</h2>
            <p className="text-[#5B5F50] text-base mt-4 max-w-xl mx-auto" style={{ fontFamily: "Inter, sans-serif" }}>
              A simple, transparent process designed to remove fear and build confidence. We make it feel safe.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((p) => (
              <div key={p.num} className="relative">
                {p.num !== "04" && (
                  <div className="hidden lg:block absolute top-10 left-[calc(100%_-_12px)] w-6 h-px bg-[#4F5D1E]/30 z-10"></div>
                )}
                <div className="bg-white border border-[#23261C]/10 rounded-2xl p-6 hover:border-[#4F5D1E]/50 transition-all duration-300 h-full">
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-bebas text-[#4F5D1E]/30 leading-none" style={{ fontSize: "48px" }}>{p.num}</span>
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#4F5D1E]/10">
                      <p.icon className="text-[#4F5D1E] w-5 h-5" />
                    </div>
                  </div>
                  <h3 className="font-display text-[#23261C] font-bold text-lg mb-3">{p.title}</h3>
                  <p className="text-[#5B5F50] text-sm leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>{p.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — off-white */}
      <section className="bg-[#F0EEE7] py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <p className="text-[#4F5D1E] text-xs font-semibold tracking-widest uppercase mb-3" style={{ fontFamily: "Inter, sans-serif" }}>SUCCESS STORIES</p>
            <h2 className="font-hero text-4xl md:text-5xl text-[#23261C] leading-tight">Client Results</h2>
          </div>

          <div className="flex items-center gap-2 mb-10 justify-center">
            <div className="inline-flex items-center gap-2 bg-[#3F4A1D] text-[#E8EDCF] px-4 py-2 rounded-md text-sm font-semibold" style={{ fontFamily: "Inter, sans-serif" }}>
              <Star className="w-4 h-4 fill-[#E8EDCF] text-[#E8EDCF]" /> 4.9 Average Rating
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white rounded-2xl p-6 border border-[#23261C]/10 hover:border-[#4F5D1E]/40 transition-all">
                <Quote className="w-5 h-5 text-[#4F5D1E] mb-3" />
                <p className="font-display text-[#23261C] text-lg leading-relaxed mb-5 italic">"{t.quote}"</p>
                <div className="flex items-center justify-between pt-4 border-t border-[#23261C]/10">
                  <div>
                    <p className="font-bold text-[#23261C] text-sm" style={{ fontFamily: "Inter, sans-serif" }}>{t.name}</p>
                    <p className="text-[#555] text-xs" style={{ fontFamily: "Inter, sans-serif" }}>{t.loc}</p>
                  </div>
                  <p className="text-[#4F5D1E] text-xs font-semibold" style={{ fontFamily: "Inter, sans-serif" }}>Score: {t.score}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — cream */}
      <section className="bg-[#F5F4EF] py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="lg:sticky lg:top-32">
              <p className="text-[#4F5D1E] text-xs font-semibold tracking-widest uppercase mb-3" style={{ fontFamily: "Inter, sans-serif" }}>COMMON QUESTIONS</p>
              <h2 className="font-hero text-4xl md:text-5xl text-[#23261C] leading-tight mb-6">Honest Answers to Hard Questions</h2>
              <p className="text-[#5B5F50] text-base leading-relaxed mb-8" style={{ fontFamily: "Inter, sans-serif" }}>
                The credit repair industry has a bad reputation — and for good reason. We address the real objections head-on because transparency is the foundation of everything we do.
              </p>
              <div className="bg-white border border-[#23261C]/10 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#4F5D1E]/15">
                    <Shield className="text-[#4F5D1E] w-4 h-4" />
                  </div>
                  <p className="text-[#23261C] font-semibold text-sm" style={{ fontFamily: "Inter, sans-serif" }}>CROA Compliant</p>
                </div>
                <p className="text-[#5B5F50] text-sm leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>We operate in full compliance with the Credit Repair Organizations Act. Your rights are protected at every step.</p>
              </div>
            </div>

            <div className="space-y-3">
              {faqs.map((f, i) => {
                const isOpen = openFaq === i;
                return (
                  <div key={f.q} className={`border rounded-2xl overflow-hidden transition-all duration-200 bg-white ${isOpen ? "border-[#4F5D1E]/50" : "border-[#23261C]/10"}`}>
                    <button onClick={() => setOpenFaq(isOpen ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left" data-testid={`faq-item-${i}`}>
                      <span className={`font-semibold text-sm leading-snug ${isOpen ? "text-[#4F5D1E]" : "text-[#23261C]/85"}`} style={{ fontFamily: "Inter, sans-serif" }}>{f.q}</span>
                      <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 ${isOpen ? "bg-[#3F4A1D] text-[#F5F4EF]" : "bg-[#23261C]/8 text-[#23261C]/60"}`}>
                        {isOpen ? <Minus className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-5">
                        <p className="text-[#5B5F50] text-sm leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>{f.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA — deep olive inverse block */}
      <section className="inverse relative py-24 overflow-hidden bg-[#2C3413]">
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#B9C77F] text-xs font-semibold tracking-widest uppercase mb-4" style={{ fontFamily: "Inter, sans-serif" }}>START TODAY</p>
              <h2 className="font-hero text-5xl md:text-6xl text-white leading-none mb-6">
                READY TO GET<br /><span className="text-[#B9C77F]">APPROVED?</span>
              </h2>
              <p className="text-white/60 text-base leading-relaxed mb-8 max-w-md" style={{ fontFamily: "Inter, sans-serif" }}>
                We'll review your credit, build your personalized plan, and walk every step with you. No empty promises. Just results.
              </p>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#B9C77F]" />
                  <span className="text-white/70 text-sm" style={{ fontFamily: "Inter, sans-serif" }}>Response within 24 hours</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-[#B9C77F]" />
                  <span className="text-white/70 text-sm" style={{ fontFamily: "Inter, sans-serif" }}>90-day money-back guarantee</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#B9C77F]" />
                  <span className="text-white/70 text-sm" style={{ fontFamily: "Inter, sans-serif" }}>CROA compliant</span>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8">
              <h3 className="font-display text-white font-bold text-xl mb-2">Book Your Free Consultation</h3>
              <p className="text-white/50 text-sm mb-6" style={{ fontFamily: "Inter, sans-serif" }}>No commitment. Just a direct plan.</p>
              <Link to="/booking" className="btn-gold w-full !bg-[#B9C77F] !text-[#23261C] hover:!bg-[#cbd79a]" data-testid="home-final-cta">
                Book Free Consultation
              </Link>
              <p className="text-center text-xs text-white/40 mt-4" style={{ fontFamily: "Inter, sans-serif" }}>
                Prefer to chat first? <Link to="/contact" className="underline text-[#B9C77F]">Send us a message</Link>
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
